import React, { Fragment } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { Tabs, Tab, Button, Form, Row } from 'react-bootstrap';
import { RtbCampaignSetupStepProps, CampaignSetupTab } from './RtbCampaignSetupStepModel';
import styles from './rtbCampaignSetupStep.module.scss';
import { EditLimitation } from 'containers/Limitations/EditLimitation';
import CampaignBasicForm from 'containers/RtbCampaigns/RtbCampaignSetupFlow/RtbCampaignForm/RtbCampaignBasicForm';
import { AdType, Gender, RTBCAMPAIGN_DEFAULT_AGE_MAX, RTBCAMPAIGN_DEFAULT_AGE_MIN } from 'core/rtbCampaign/RtbCampaign';
import { Formik } from 'formik';
import i18n from 'i18next';
import Select from 'components/Select/Select';
import { ageMaxOptions, ageMinOptions, genderOptions, getRtbAgeGroupsByAgeRange } from 'core/limitation/l2ObjectTAOptions';
import { defaultTo, get, isEmpty, partial, remove } from 'lodash';
import { SearchKeywordsComponent } from 'containers/Limitations/SearchKeywordsComponent/DefaultSearchKeywordsComponent';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { AlertModalData, RtbCampaignBasicFormState } from '../RtbCampaignForm/RtbCampaignBasicFormModel';
import { Modal } from 'components/Modal/Modal';

export class RtbCampaignSetupStep extends React.Component<RtbCampaignSetupStepProps> {

  componentDidUpdate () {
    this.scrollToErrorField();
  }

  componentWillUnmount () {
    this.props.model.onUnmount();
  }

  handleSubmit = async (values) => {};

  validate = (campaign) => {
    return this.props.model.validateCampaignBasic(campaign);
  }

  scrollToErrorField () {
    const node = ReactDOM.findDOMNode(this);
    if (node instanceof HTMLElement) {
      const child = node.querySelector('.errorTip');
      child && child.scrollIntoView({
        block: 'center'
      });
    }
  }

  getOptValueOfTA = (includeTA, preferredTA) => {
    const defaultOptValue = 'preferred';
    return includeTA ?
      'include' :
      preferredTA ?
        'preferred' :
        defaultOptValue;
  }

  renderAgeAndGender = (limitationValueSet, onChange: (operate: string, type: string, value: any) => void) => {
    const generalTAInclude = defaultTo(limitationValueSet['include'], []);
    const generalTAPreferred = defaultTo(limitationValueSet['preferred'], []);
    const ageMinInclude = generalTAInclude.find(value => value.type === 'age_min');
    const ageMinPreferred = generalTAPreferred.find(value => value.type === 'age_min');
    const ageMaxInclude = generalTAInclude.find(value => value.type === 'age_max');
    const ageMaxPreferred = generalTAPreferred.find(value => value.type === 'age_max');
    const genderInclude = generalTAInclude.find(value => value.type === 'gender');
    const genderPerferred = generalTAPreferred.find(value => value.type === 'gender');

    const ageOptValue = this.getOptValueOfTA(ageMinInclude, ageMinPreferred);
    const genderOptValue = this.getOptValueOfTA(genderInclude, genderPerferred);

    const ageMinLimitation = ageMinInclude || ageMinPreferred;
    const ageMin = get(ageMinLimitation, 'value', RTBCAMPAIGN_DEFAULT_AGE_MIN);

    const ageMaxLimitation = ageMaxInclude || ageMaxPreferred;
    const ageMax = get(ageMaxLimitation, 'value', RTBCAMPAIGN_DEFAULT_AGE_MAX);

    const gendersLimitation = genderInclude || genderPerferred;
    const gender = get(gendersLimitation, 'value[0]', genderOptions.find(option => option.value === Gender.ALL));

    const onAgeMinChange = newAgeMin => {
      if (newAgeMin > ageMax) {
        onChange(ageOptValue, 'age_max', newAgeMin);
      }
      onChange(ageOptValue, 'age_min', newAgeMin);
    };
    const onAgeMaxChange = newAgeMax => {
      if (newAgeMax < ageMin) {
        onChange(ageOptValue, 'age_min', newAgeMax);
      }
      onChange(ageOptValue, 'age_max', newAgeMax);
    };
    const onAgeOptChange = (e) => {
      const opts = ['preferred', 'include'];
      const optsToSetValue = remove(opts, opt => opt === e.target.value);
      const optToSetValue = defaultTo(optsToSetValue[0], 'preferred');
      const optToSetUndefined = defaultTo(opts[0], 'include');
      onChange(optToSetValue, 'age_min', ageMin);
      onChange(optToSetValue, 'age_max', ageMax);
      onChange(optToSetUndefined, 'age_min', undefined);
      onChange(optToSetUndefined, 'age_max', undefined);
    };

    const getGenderValue = gender => gender.value === -1 ? undefined : [gender];
    const onGendersChange = gender => {
      onChange(genderOptValue, 'gender', getGenderValue(gender));
    };
    const onGenderOptChange = (e) => {
      const opts = ['preferred', 'include'];
      const optsToSetValue = remove(opts, opt => opt === e.target.value);
      const optToSetValue = defaultTo(optsToSetValue[0], 'preferred');
      const optToSetUndefined = defaultTo(opts[0], 'include');
      onChange(optToSetValue, 'gender', getGenderValue(gender));
      onChange(optToSetUndefined, 'gender', undefined);
    };

    const ageGroupsHint = getRtbAgeGroupsByAgeRange(ageMin, ageMax).map(group => group.label).join(',');

    return (
      <div>
        <Form.Group as={Row} controlId={'age'}>
          <Form.Label>{i18n.t<string>('limitation.labels.age')}</Form.Label>
          <div>
            <div className={styles.ageSelector}>
              <Select
                className={styles.ageMin}
                options={ageMinOptions}
                name='ageMin'
                simpleValue
                value={ageMin}
                onChange={onAgeMinChange}
              />
              ~
              <Select
                className={styles.ageMax}
                options={ageMaxOptions}
                name='ageMax'
                simpleValue
                value={ageMax}
                onChange={onAgeMaxChange}
              />
            </div>
            <span className={styles.ageGroupsHint}>{i18n.t<string>('limitation.hints.ageGroups', { groups: ageGroupsHint })}</span>
          </div>
        </Form.Group>
        <Fragment>
          <Form.Group as={Row} controlId={'age-opt-preferred'} style={{ 'marginBottom': 0 }}>
            <Form.Label>{''}</Form.Label>
            <div className={styles.optRadio}>
              <Form.Check
                type={'radio'}
                label={i18n.t<string>('rtbCampaign.limitation.operationSet.preferred')}
                value={'preferred'}
                id={'age_preferred'}
                onChange={onAgeOptChange}
                checked={ageOptValue === 'preferred'}
                disabled={false}
              />
            </div>
          </Form.Group>
          <Form.Group as={Row} controlId={'age-opt-include'}>
            <Form.Label>{''}</Form.Label>
            <div className={styles.optRadio}>
              <Form.Check
                type={'radio'}
                label={i18n.t<string>('rtbCampaign.limitation.operationSet.include')}
                value={'include'}
                id={'age_include'}
                onChange={onAgeOptChange}
                checked={ageOptValue === 'include'}
                disabled={false}
              />
            </div>
          </Form.Group>
        </Fragment>
        <Form.Group as={Row} controlId={'gender'}>
          <Form.Label>{i18n.t<string>('limitation.labels.gender')}</Form.Label>
          <Select
            options={genderOptions}
            value={gender}
            onChange={onGendersChange}
          />
        </Form.Group>
        {gender.value !== -1 &&
         <Fragment>
          <Form.Group as={Row} controlId={'gender-opt-preferred'} style={{ 'marginBottom': 0 }}>
            <Form.Label>{''}</Form.Label>
            <div className={styles.optRadio}>
              <Form.Check
                type={'radio'}
                label={i18n.t<string>('rtbCampaign.limitation.operationSet.preferred')}
                value={'preferred'}
                id={'gender_preferred'}
                onChange={onGenderOptChange}
                checked={genderOptValue === 'preferred'}
                disabled={false}
              />
            </div>
          </Form.Group>
          <Form.Group as={Row} controlId={'gender-opt-include'}>
            <Form.Label>{''}</Form.Label>
            <div className={styles.optRadio}>
              <Form.Check
                type={'radio'}
                label={i18n.t<string>('rtbCampaign.limitation.operationSet.include')}
                value={'include'}
                id={'gender_include'}
                onChange={onGenderOptChange}
                checked={genderOptValue === 'include'}
                disabled={false}
              />
            </div>
          </Form.Group>
         </Fragment>
        }
      </div>
    );
  }

  renderAlertModalData = (alertModalData: AlertModalData) => {
    const { title, message, dismiss, onConfirm, onCancel, primaryTitle, secondaryTitle } = alertModalData;
    return (
      <Modal
        title={title}
        dismiss={dismiss}
        primaryButton={{
          title: primaryTitle || i18n.t<string>('common.buttons.confirm'),
          callback: onConfirm
        }}
        secondaryButton={{
          title: secondaryTitle || i18n.t<string>('common.buttons.cancel'),
          callback: onCancel
        }}
      >
        {message}
      </Modal>
    );
  }

  renderFormContent = (formikProps) => {
    const model = this.props.model;
    const order = model.flowModel.order;
    const campaignData = model.flowModel.state.campaign;
    const basicFormModel = model.getRtbCampaignBasicFormModel(campaignData.basic.adType);
    if (!basicFormModel) {
      return <LoadingIndicator/>;
    }
    const limitationModel = model.getLimitationModel();
    model.setFormikProps(formikProps);
    const onTabSelect = (tab) => {
      if (tab === null) {
        return;
      }
      model.goSubStep(+tab);
    };
    const needSetupAgeAndGender = get(basicFormModel, 'needSetupAgeGender', false);
    const isKeywordCampaign = campaignData.basic.adType === AdType.KEYWORD;
    const goLastEnabled = model.flowModel.type === 'create' || isKeywordCampaign;
    const products = defaultTo(campaignData.products, []);
    const formModelState: RtbCampaignBasicFormState = basicFormModel.state;

    const handleOnEnterBasicTab = () => {
      if (!basicFormModel || !limitationModel) {
        return;
      }
      const limitationValue = limitationModel.limitationValue;
      const fetchRecommendBidPrice = async () => basicFormModel.fetchRecommendBidPrice(limitationValue);
      basicFormModel.processAlertStep(limitationModel, fetchRecommendBidPrice, partial(onTabSelect, CampaignSetupTab.LIMITATION));
    };
    const handleOnClickUpdateCampaignAnalytics = () => {
      const campaignData = {
        basic: { ...formikProps.values },
        limitations: limitationModel.limitationValue,
        products: model.flowModel.state.campaign.products
      };
      model.flowModel.fetchCampaignAnalytics(campaignData);
    };
    // 「出價試算」面板需要的所有外部接縫。合併成單一 prop 的理由：
    // 這些值全都只在這個 render scope 裡拿得到（basicFormModel 與 formikProps 都是
    // 這裡的區域變數），拆成六個 prop 只是把同一件事的簽章寬度乘六。
    //
    // 兩個 model 方法要 bind：它們是 KeywordFormModel 的實例方法、內部用 this，
    // 直接取參考會掉 receiver。兩者都是 optional（只有關鍵字型活動有）。
    //
    // 「帶入下一步」＝ setFieldValue('bidPrice', ...)：`優化目標` 與 `基本資訊`
    // 不是兩個 step，是同一個 <Formik> 底下的兩個 Tab，所以不需要 lift state / store。
    const handleSetBidPrice = (bidPrice: number) => {
      formikProps.setFieldValue('bidPrice', bidPrice);
    };
    const bidPriceReference = {
      optimize: formikProps.values.optimize,
      // 經銷商出價下限。刻意與 optimize 由同一個 formikProps.values.optimize 導出,
      // 兩者必須同步 —— 分別取會讓區間 clamp 用到上一次的優化目標。
      bidFloor: basicFormModel.getBidPriceRange(formikProps.values.optimize).min,
      currency: basicFormModel.currency,
      currentBidPrice: formikProps.values.bidPrice,
      // 建立模式的 values.bidPrice 是 setupDefaultCampaign 塞的預設值，不是使用者
      // 設過的出價 ⇒ 此時不顯示「此活動已有出價設定」警示條。
      hasExistingBid: model.flowModel.type !== 'create',
      rememberKeywordRows: basicFormModel.rememberKeywordRows?.bind(basicFormModel),
      recallKeywordRow: basicFormModel.recallKeywordRow?.bind(basicFormModel),
      getRecommendBidPriceRangeOf: basicFormModel.getRecommendBidPriceRangeOf?.bind(basicFormModel),
      setBidPrice: handleSetBidPrice
    };
    const handleOnClickCompleteAndCheck = () => {
      if (!basicFormModel || !limitationModel) {
        return;
      }
      isEmpty(formikProps.errors) && model.activeTab !== CampaignSetupTab.BASIC
        ? basicFormModel.processAlertStep(limitationModel, model.goNext, partial(onTabSelect, CampaignSetupTab.LIMITATION))
        : model.goNext();
    };
    return (
      <>
        {formModelState.loading && <LoadingIndicator />}
        {formModelState.alertModalData &&
          this.renderAlertModalData(formModelState.alertModalData)}
        <Tabs
          id='campaignFromTab'
          activeKey={model.activeTab}
          onSelect={onTabSelect}
        >
          <Tab
            eventKey={CampaignSetupTab.LIMITATION}
            title={i18n.t<string>('stepSideBar.labels.targeting')}
          >
            {isKeywordCampaign ? (
              <SearchKeywordsComponent
                // Remount component when tab changed
                // to avoid the issue that the input keyword not refreshed
                // after KeywordFormModel call limitationModel.setLimitation function
                key={model.activeTab}
                limitationModel={limitationModel}
                products={products}
                bidPriceReference={bidPriceReference}
              />
            ) : (
              <EditLimitation
                model={limitationModel}
                order={order}
                renderGeneralFields={
                  needSetupAgeAndGender ? this.renderAgeAndGender : undefined
                }
              />
            )}
          </Tab>
          <Tab
            eventKey={CampaignSetupTab.BASIC}
            title={i18n.t<string>('stepSideBar.labels.basic')}
            onEnter={handleOnEnterBasicTab}
          >
            <CampaignBasicForm model={basicFormModel} />
          </Tab>
        </Tabs>
        <div className={styles.buttonArea}>
          <Button
            variant='primary'
            size='sm'
            onClick={handleOnClickCompleteAndCheck}
          >
            {i18n.t<string>('campaign.buttons.completeAndCheck')}
          </Button>
          {model.flowModel.refreshCampaignAnalyticsEnabled && (
            <Button
              variant='secondary'
              size='sm'
              onClick={handleOnClickUpdateCampaignAnalytics}
            >
              {i18n.t<string>('campaign.buttons.updateCampaignAnalytics')}
            </Button>
          )}
          {goLastEnabled && (
            <Button variant='secondary' size='sm' onClick={model.goLast}>
              {i18n.t<string>('campaign.buttons.back')}
            </Button>
          )}
        </div>
      </>
    );
  }

  render () {
    const model = this.props.model;
    const campaignData = model.flowModel.state.campaign;
    const isKeywordCampaign = campaignData.basic.adType === AdType.KEYWORD;
    const setupStepClassName = classNames(styles.setupStep, {
      [styles.keywordCampaign]: isKeywordCampaign,
      [styles.keywordLimitationTab]: isKeywordCampaign && model.activeTab === CampaignSetupTab.LIMITATION
    });
    return (
      <div className={setupStepClassName}>
        <Formik
          initialValues={campaignData.basic}
          enableReinitialize
          onSubmit={this.handleSubmit}
          validate={this.validate}
          validateOnBlur={false}
        >
          {this.renderFormContent}
        </Formik>
      </div>
    );
  }
}
